import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, CLIENT_ERROR } from './api.js';

/**
 * Fetch-on-mount with the three states a screen actually has.
 *
 * ── Why not react-query, which the old portal used ───────────────────
 *
 * Every screen here does the same thing: load a list once, show it, refetch
 * after a mutation. There is no cache invalidation graph, no pagination cursor,
 * no optimistic update, no background refetch. react-query is a good library
 * for a problem this app does not have, and it brings a provider, a devtools
 * decision, and a set of defaults that have to be understood before the first
 * bug makes sense.
 *
 * ── The three states are the point ───────────────────────────────────
 *
 * `loading`, `failed` and `data` are kept distinct because collapsing them is
 * the exact defect this project shipped twice: the login screen rendered a
 * failed request as "signed out" (auth#32), and the old portal's Overview
 * guarded with `isLoading ? '—' : d!.stats`, where `isLoading` is false in
 * states where `data` is undefined — a non-null assertion that threw.
 *
 * So: no non-null assertions on fetched data, and a failure is never rendered
 * as an empty list. An empty list means the server said there is nothing; a
 * failure means nobody knows.
 */
export type Async<T> =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; data: T };

export function useAsync<T>(fetcher: () => Promise<T>, deps: unknown[] = []): {
  state: Async<T>;
  /**
   * Refetch. `reload(true)` refetches QUIETLY — without dropping back to the
   * loading state first (org-admin#57).
   *
   * The default is unchanged and is right for a reload somebody triggered: they
   * pressed a thing, so the screen should visibly respond.
   *
   * A POLLING screen is the opposite case. The Uptime section refetches as often
   * as every ten seconds, and a loading state on each tick would blank the table
   * and rebuild it — a page that flickers every ten seconds and is unreadable
   * for a fraction of each one. Quiet keeps the current data on screen until the
   * new data replaces it.
   *
   * A quiet refetch that FAILS still surfaces the failure. That is deliberate
   * and matches the rest of this module: a status screen silently holding stale
   * data while the request behind it is failing is the same defect as rendering
   * a failure as an empty list, just slower to notice.
   */
  reload: (quiet?: boolean) => void;
} {
  const [state, setState] = useState<Async<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  /** Read and cleared by the effect below. A ref rather than state, because it
   *  must not itself cause the render that runs the effect. */
  const quiet = useRef(false);

  const reload = useCallback((isQuiet = false) => {
    quiet.current = isQuiet;
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    let live = true;
    if (!quiet.current) setState({ status: 'loading' });
    // Cleared immediately: the flag describes THIS refetch, and leaving it set
    // would make the next dependency-driven fetch silently quiet too.
    quiet.current = false;
    fetcher()
      .then((data) => live && setState({ status: 'ready', data }))
      .catch((e: unknown) => {
        if (!live) return;
        setState({ status: 'failed', message: describe(e) });
      });
    return () => {
      // StrictMode mounts twice in development, and an unguarded setState after
      // unmount is the warning that trains people to disable StrictMode — which
      // is how the old portal lost the check that would have caught its own
      // white-screen bug.
      live = false;
    };
    // `fetcher` is deliberately not a dependency. Callers pass an inline arrow,
    // so it is a new function on every render and depending on it would refetch
    // in a loop. The caller declares what the fetch actually depends on via
    // `deps` — which is the same contract `useCallback` has, and the same way to
    // get it wrong.
  }, [...deps, nonce]);

  return { state, reload };
}

/**
 * The write side: run something, report what happened, never leave the button
 * looking clickable while it is in flight.
 *
 * `running` exists to prevent the double-submit, which on this surface is not a
 * cosmetic problem — publishing an agreement version twice is two immutable
 * rows and a fan-out to every affected user.
 *
 * The outcome is a value rather than a toast because it belongs next to the
 * control that produced it. A toast that has already faded is indistinguishable
 * from an action that reported nothing.
 */
export function useAction(): {
  running: boolean;
  outcome: { ok: boolean; message: string } | null;
  /** Resolves true when `fn` succeeded. A caller that reloads afterwards
   *  must reload only then: a reload replaces the panel, and with it the
   *  failure message this just set, so a refused action reads as a refresh
   *  that did nothing (found on the Access tab, 2026-09-27). */
  run: (fn: () => Promise<string>) => Promise<boolean>;
  clear: () => void;
} {
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<{ ok: boolean; message: string } | null>(null);

  const run = useCallback(async (fn: () => Promise<string>) => {
    setRunning(true);
    setOutcome(null);
    try {
      setOutcome({ ok: true, message: await fn() });
      return true;
    } catch (e: unknown) {
      setOutcome({ ok: false, message: describe(e) });
      return false;
    } finally {
      // In `finally`, so a throw cannot leave every button on the screen
      // permanently disabled with no way back but a reload.
      setRunning(false);
    }
  }, []);

  return { running, outcome, run, clear: useCallback(() => setOutcome(null), []) };
}

/**
 * A 404 from an admin path is far more likely to be a missing ROLE than a
 * missing endpoint, because the staff guard answers 404 rather than 403 so the
 * surface is not advertised. Saying so here turns a wall of "not found" across
 * every screen into one sentence that names the actual cause.
 */
export function describe(e: unknown): string {
  if (e instanceof ApiError) {
    /**
     * Ours passes straight through. The rewrite below is about the ADMIN API's
     * 404s; a client-raised one — "no account has that id" — reached the server
     * fine, and telling the reader they might lack the admin role would send
     * them to check a permission that is not the problem.
     */
    if (e.code === CLIENT_ERROR) return e.message;
    if (e.status === 404) {
      return 'Not found (404). On an admin endpoint this usually means this account lacks the admin role, rather than the endpoint being missing.';
    }
    if (e.status === 401) return 'Your session has expired. Sign in again.';
    return e.message;
  }
  // A TypeError from fetch is the CORS/offline case, and its message is
  // famously unhelpful — naming the likely cause beats repeating "Failed to
  // fetch".
  if (e instanceof TypeError) {
    return 'Could not reach the admin API. This is usually CORS, a wrong VITE_AUTH_URL, or the service being down.';
  }
  // Any other Error already carries a sentence written for a person (the CI
  // fleet screen's token and client do). `String(e)` would prefix its class.
  if (e instanceof Error) return e.message;
  return String(e);
}
